import isEmpty from 'lodash/isEmpty';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { TiArrowUpThick } from 'react-icons/ti';
import ScrollToTop from 'react-scroll-to-top';
import CardMainNews from 'src/components/cards/card-main-news';
import Carousels from 'src/components/carousels';
import { SHOW_COMMEMORATE } from 'src/constants';
import { WEB_TITLE } from 'src/constants/layout';
import { NAV_DATA } from 'src/redux/navSlice';
import { useAppDispatch } from 'src/redux/store';
import { DATALAYER_TYPE } from 'src/utils/datalayer/send-datalayer';
import useResponsive from 'src/utils/device';
import styled from 'styled-components';

const Header = dynamic(import('src/components/layouts/header'), { ssr: true });
const Footer = dynamic(import('src/components/layouts/footer'));

const Layout = ({ children, data, sessionSevId, newHighlightOne, isHomePage = false }: any) => {
  const router = useRouter();
  // const isHomePage = router.pathname === '/' || router.asPath === '/';
  const dispatch = useAppDispatch();
  const [isSticky, setIsSticky] = useState(false);
  const responsive = useResponsive();
  // const [isMobile, setIsMobile] = useState(false);
  const heroContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const find = data?.header?.filter((i: any) => i.nameEng === router?.query?.cid);
    if (!isEmpty(find)) {
      dispatch(NAV_DATA(find));
    }
  }, []);

  useEffect(() => {
    if (!isHomePage) return;

    const handleScroll = () => {
      if (heroContainerRef.current) {
        const rect = heroContainerRef.current.getBoundingClientRect();
        setIsSticky(rect.top <= -80);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  useEffect(() => {
    if (isHomePage) return;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const triggerPoint = window.innerHeight * 0.15;
      setIsSticky(scrollY > triggerPoint);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  //  แก้ accessibility issue ของ react-slick
  //    react-slick ใส่ aria-hidden="true" บน slide ที่ไม่ active แต่ไม่ได้ set inert
  //    → keyboard user สามารถ Tab เข้าไปหา link ที่มองไม่เห็นได้
  //    แก้โดย sync aria-hidden เป็น inert attribute (ซึ่งจะ disable focus + a11y tree ทั้ง subtree)
  useEffect(() => {
    const syncInertWithAriaHidden = () => {
      document.querySelectorAll('.slick-slide').forEach(slide => {
        const isHidden = slide.getAttribute('aria-hidden') === 'true';
        if (isHidden) {
          slide.setAttribute('inert', '');
        } else {
          slide.removeAttribute('inert');
        }
      });
    };

    //  รัน initial
    syncInertWithAriaHidden();

    //  watch การเปลี่ยน aria-hidden (เกิดทุกครั้งที่ slide เปลี่ยน)
    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
          const target = mutation.target as HTMLElement;
          if (target.classList.contains('slick-slide')) {
            const isHidden = target.getAttribute('aria-hidden') === 'true';
            if (isHidden) {
              target.setAttribute('inert', '');
            } else {
              target.removeAttribute('inert');
            }
          }
        }
      }
    });

    //  observe ทั้ง body (เผื่อ carousel ถูก mount หลัง หรือมีหลายตัว)
    observer.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ['aria-hidden']
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      {isHomePage ? (
        <>
          <HeroContainer ref={heroContainerRef}>
            <HeaderWrapper className={isSticky ? 'sticky' : 'overlay'}>
              <h1 className='h1-seo'>{WEB_TITLE}</h1>
              <Header data={data?.header} sessionSevId={sessionSevId} isOverlay={!isSticky} />
            </HeaderWrapper>

            <HeroWrapper>
              <div className='carousels-hero' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
                <Carousels slideShow={1} autoplaySpeed={7000} infinite={true} arrows={true} dots={true}>
                  {newHighlightOne?.map((item: any, index: number) => (
                    <div key={index} className='card-item'>
                      <CardMainNews
                        data={item}
                        showTitle={true}
                        showBlurb={false}
                        showCategory={true}
                        priority={true}
                        type={responsive.isMobile ? 'CARD_LARGE' : 'FEATURE_IMAGE'}
                        vertical_image={true}
                        eventDataLayer={{
                          type: DATALAYER_TYPE.TRACK_INDEX,
                          router: router?.pathname,
                          section: 'section-highlight',
                          data: {
                            title: item?.title,
                            heading: 'highlight',
                            index: index + 1
                          }
                        }}
                      />
                    </div>
                  ))}
                </Carousels>
              </div>
            </HeroWrapper>
          </HeroContainer>

          {children}
        </>
      ) : (
        <>
          {responsive.isMobile ? (
            <HeaderWrapper className='sticky overflow' $isOtherPage>
              <Header data={data?.header} sessionSevId={sessionSevId} isOverlay={false} isHomePage={isHomePage} />
            </HeaderWrapper>
          ) : (
            <>
              <Header data={data?.header} sessionSevId={sessionSevId} isOverlay={false} />
            </>
          )}
          {responsive.isMobile && <HeaderSpacer />}
          {children}
        </>
      )}

      <ScrollToTop
        smooth
        color='#ffe600'
        component={
          <div className='scroll-to-top' aria-label='Scroll to top'>
            <TiArrowUpThick />
          </div>
        }
      />
      <Footer data={data?.footer} />
    </>
  );
};

export default Layout;

const HeaderSpacer = styled.div`
  height: 60px;
  @media (max-width: 768px) {
    height: 103px;
  }
`;

const HeroContainer = styled.div`
  position: relative;
  width: 100%;
`;

interface HeaderWrapperProps {
  $isOtherPage?: boolean;
}

const HeaderWrapper = styled.div<HeaderWrapperProps>`
  z-index: 100;
  transition: all 0.3s ease;

  &.overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;

    header {
      background: transparent !important;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 150px;
        background: linear-gradient(rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 1) 55%, rgba(0, 0, 0, 0.5) 75%, rgba(0, 0, 0, 0) 90%, rgba(0, 0, 0, 0) 100%);
        pointer-events: none;
        z-index: -1;
      }
    }

    .mobile-logo img {
      height: 55px;
      transition: height 0.3s ease;
      @media (max-width: 768px) {
        height: 50px;
      }
    }

    .mobile-search-box {
      opacity: 1;
      max-height: 60px;
      transition: all 0.3s ease;
    }
  }
  &.overflow {
    @media (max-width: 768px) {
      height: 103px;
      overflow: hidden;
    }
  }

  &.sticky {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;

    header {
      background-color: #000000ed !important;
    }
    .expand-more-menu {
      background-color: #000000ed !important;
      box-shadow: 0px 5px 6px -6px rgba(0, 0, 0, 0.3);
    }
    .mobile-logo img {
      height: 35px;
      transition: height 0.3s ease;
      @media (max-width: 768px) {
        height: 32px;
        width: 104px;
      }
    }

    .mobile-header {
      gap: 10px;
      .mobile-left {
        justify-content: flex-start;
      }
      .mobile-icons {
        justify-content: flex-end;
      }
    }

    .nav-container {
      padding: 10px 15px 0px;
      @media (max-width: 768px) {
        padding: 12px 12px;
      }
    }
  }
`;

const HeroWrapper = styled.div`
  position: relative;
  width: 100%;
  background-color: black;
  overflow: hidden;
  @media (max-width: 768px) {
    border-radius: 0px 0px 40px 40px;
    box-shadow: 1px 4px 4px rgba(0, 0, 0, 0.2);
  }
  .carousels-hero {
    width: 100%;
    max-width: 2000px;
    margin: 0 auto;
    /* min-height: 595px; */
    .card-main-news {
      border-radius: 0px;
      @media (max-width: 768px) {
        border-radius: 0px 0px 40px 40px;
      }
    }

    .card-content {
      padding: 5rem 5rem;
      background: linear-gradient(rgba(0, 0, 0, 0) 0%, rgb(0, 0, 0) 100%);
      .category {
        font-size: 1.45rem;
        border-left: 5px solid #ed1a3d;
        line-height: 1.25;
      }
      .title {
        font-size: 1.75rem;
      }
      @media (max-width: 768px) {
        .category {
          font-size: 1.05rem;
          border-left: 5px solid #ed1a3d;
          line-height: 1.25;
        }
        .title {
          font-size: 1.25rem;
        }
      }
    }

    .slick-dots {
      bottom: 50px;
      text-align: left;
      padding-left: 5rem;
      li {
        width: 50px;
        height: 10px;
        margin: 0 5px;
        button {
          width: 100%;
          height: 100%;
          padding: 0;
          &:before {
            content: '';
            width: 100%;
            height: 100%;
            background-color: #ffffff;
            opacity: 1;
            border-radius: 0;
            position: absolute;
            top: 0;
            left: 0;
            @media (max-width: 768px) {
              border-radius: 50px;
            }
          }
        }
        &.slick-active button:before {
          background-color: #ed1a3d;
        }
      }
    }

    .slick-prev,
    .slick-next {
      z-index: 10;
      :before {
        color: #ffffff !important;
        font-size: 48px !important;
        @media (max-width: 768px) {
          font-size: 30px !important;
        }
      }
    }
    .slick-prev {
      left: 50px;
      @media (max-width: 768px) {
        left: 20px;
      }
    }
    .slick-next {
      right: 50px;
      @media (max-width: 768px) {
        right: 25px;
      }
    }

    @media (min-width: 768px) and (max-width: 1280px) {
      .card-thumb {
        aspect-ratio: 5 / 4;
        background-color: black;
        padding: 40px 0px;
      }
    }

    @media (max-width: 768px) {
      .card-content {
        padding: 4.5rem 2rem;
        background: linear-gradient(transparent 0%, transparent 20%, rgba(0, 0, 0, 0.3) 35%, rgba(0, 0, 0, 0.7) 45%, black 50%, black 100%);
      }
      .card-thumb {
        aspect-ratio: 9 / 16;
        background-color: black;
        padding: 80px 0px;
      }
      .slick-dots {
        padding-left: 2rem;
        li {
          width: 38px;
          height: 8px;
          margin: 0 3px;
        }
      }
    }
  }
`;
